import { Modal as ModalMUI } from '@mui/material';
import React from 'react';
import styled, { css } from 'styled-components';
import useTracking, { TrackingEventData } from '../hooks/useTracking';
import { BACKGROUND, BORDER_GRAY, WHITE } from '../theme';
import { EventNames } from '../utils/trackingEvents';

const StyledModalHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;

  padding: 16px;
  border-bottom: 1px solid ${BORDER_GRAY};
`;

export const MODAL_CONTENT_BASE_STYLE = css`
  max-height: 70vh;
  padding: 16px 16px 40px 16px;

  background-color: ${BACKGROUND};

  display: flex;
  flex-direction: column;

  overflow-y: auto;
  box-sizing: border-box;
`;

const ModalHeader = ({
  testId = 'modal-header',
  children,
}: {
  testId?: string;
  children: React.ReactNode;
}) => <StyledModalHeader data-testid={testId}>{children}</StyledModalHeader>;

const StyleModalContent = styled.div`
  ${MODAL_CONTENT_BASE_STYLE}

  gap: 16px;

  overflow-y: auto;
`;

const ModalContent = ({
  testId = 'modal-content',
  children,
  style,
}: {
  testId?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <StyleModalContent style={style} data-testid={testId}>
    {children}
  </StyleModalContent>
);

const StyledModalFooter = styled.footer`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px;
  height: 56px;
  background: ${WHITE};
  border-top: 1px solid ${BORDER_GRAY};
  border-radius: 0 0 8px 8px;

  box-sizing: border-box;
`;

const ModalFooter = ({
  testId = 'modal-footer',
  children,
}: {
  testId?: string;
  children: React.ReactNode;
}) => <StyledModalFooter data-testid={testId}>{children}</StyledModalFooter>;

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  withPadding?: boolean;
  children?: React.ReactNode;
  testId?: string;
  width?: string;
  trackingData?: Partial<TrackingEventData>;
}

interface IModal extends React.FC<ModalProps> {
  Header: typeof ModalHeader;
  Content: typeof ModalContent;
  Footer: typeof ModalFooter;
}

const Container = styled.div<{ $withPadding?: boolean; $width?: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ $width }) => $width || '550px'};
  background-color: ${WHITE};
  border-radius: 8px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));

  border: 1px solid ${BORDER_GRAY};
  border-radius: 8px;

  box-sizing: border-box;

  ${({ $withPadding }) =>
    $withPadding
      ? css`
          padding: 16px 24px 8px;
        `
      : css`
          padding: 0;
        `}
`;

const Modal: IModal = ({
  isOpen,
  onClose,
  withPadding = false,
  children,
  testId,
  width,
  trackingData,
}) => {
  const { track } = useTracking();
  return (
    <ModalMUI
      open={isOpen}
      onClose={(_, reason) => {
        if (trackingData) {
          const eventName = {
            backdropClick: EventNames.ModalBackDropClick,
            escapeKeyDown: EventNames.ModalEscapeKeyPress,
          }[reason];

          track(eventName, trackingData);
        }

        onClose?.();
      }}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      data-testid={testId}
    >
      <Container $width={width} $withPadding={withPadding}>
        {children}
      </Container>
    </ModalMUI>
  );
};

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;

export default Modal;
